import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Layers, RefreshCw, TrendingUp, TrendingDown, Zap } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const SYMBOLS = ["BTC", "ETH", "SOL", "BNB", "XRP", "DOGE"];
const RANGES = [
  { label: "0.5%", value: 0.5 },
  { label: "1%",   value: 1.0 },
  { label: "2%",   value: 2.0 },
  { label: "5%",   value: 5.0 },
];

function fmtUsd(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtPrice(p) {
  if (p == null) return "—";
  if (p >= 1000) return p.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (p >= 1)    return p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return p.toFixed(4);
}

export default function OrderbookView() {
  const [symbol, setSymbol]     = useState("BTC");
  const [rangePct, setRangePct] = useState(2.0);
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const lastFetchRef = useRef(0);

  const fetchBook = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/orderbook/aggregate/${symbol}?levels=20&range_pct=${rangePct}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) { setError("Market depth unavailable"); return; }
        if (d.error) { setError("Market depth unavailable"); return; }
        setData(d);
        lastFetchRef.current = Date.now();
      })
      .catch(() => setError("Market depth unavailable"))
      .finally(() => setLoading(false));
  }, [symbol, rangePct]);

  useEffect(() => {
    fetchBook();
    const t = setInterval(fetchBook, 10000); // refresh every 10s
    return () => clearInterval(t);
  }, [fetchBook]);

  // Compute max size for proportional bars
  const maxSize = useMemo(() => {
    if (!data) return 1;
    const all = [...(data.bids || []), ...(data.asks || [])];
    return Math.max(1, ...all.map(b => b.size_usd || 0));
  }, [data]);

  const imbColor = !data ? "rgba(255,255,255,0.4)"
    : data.imbalance === "BID HEAVY"  ? "#4ade80"
    : data.imbalance === "ASK HEAVY"  ? "#ff5d6c"
    : "#f5b042";

  return (
    <section className="hc-panel hub-section-orderbook">
      <header className="hc-panel-hdr">
        <Layers size={14} style={{ color: "#4ad6ff" }} />
        <span>Multi-Exchange Orderbook</span>
        {data && (
          <span className="hc-badge" style={{ color: imbColor, borderColor: `${imbColor}55`, background: `${imbColor}15` }}>
            {data.imbalance}
          </span>
        )}
        <button type="button" className="hc-news-refresh" onClick={fetchBook} style={{ marginLeft: "auto" }}>
          <RefreshCw size={11} className={loading ? "hc-spin" : ""} />
        </button>
      </header>

      {/* Symbol selector + range selector + summary */}
      <div className="ob-controls">
        <div className="ob-sym-group">
          {SYMBOLS.map(s => (
            <button
              key={s}
              className={`ob-sym-btn${symbol === s ? " is-active" : ""}`}
              onClick={() => setSymbol(s)}
            >{s}</button>
          ))}
        </div>
        <div className="ob-range-group">
          {RANGES.map(r => (
            <button
              key={r.value}
              className={`ob-range-btn${rangePct === r.value ? " is-active" : ""}`}
              onClick={() => setRangePct(r.value)}
            >±{r.label}</button>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      {data && !error && (
        <div className="ob-summary">
          <div className="ob-summary-item">
            <span className="ob-summary-lbl">MARK</span>
            <span className="ob-summary-val">${fmtPrice(data.mark_price)}</span>
          </div>
          <div className="ob-summary-item">
            <span className="ob-summary-lbl">SPREAD</span>
            <span className="ob-summary-val">{data.spread_pct}%</span>
          </div>
          <div className="ob-summary-item">
            <span className="ob-summary-lbl">BIDS</span>
            <span className="ob-summary-val" style={{ color: "#4ade80" }}>{fmtUsd(data.total_bid_usd)}</span>
          </div>
          <div className="ob-summary-item">
            <span className="ob-summary-lbl">ASKS</span>
            <span className="ob-summary-val" style={{ color: "#ff5d6c" }}>{fmtUsd(data.total_ask_usd)}</span>
          </div>
          <div className="ob-summary-item">
            <span className="ob-summary-lbl">RATIO</span>
            <span className="ob-summary-val" style={{ color: imbColor }}>{data.bid_ask_ratio?.toFixed(2)}</span>
          </div>
          <div className="ob-summary-item" style={{ marginLeft: "auto" }}>
            <span className="ob-summary-lbl">EXCH</span>
            <span className="ob-summary-val" style={{ color: "#4ad6ff" }}>
              {data.exchanges_used?.length || 0}/{data.exchanges_attempted?.length || 0}
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="hc-trade-empty">
          <span className="hc-empty-icon"><Layers size={16} /></span>
          <span>{error}</span>
          <small>Reconnecting to aggregated exchange order books.</small>
        </div>
      )}

      {!error && data && (
        <>
          {/* Wall summary */}
          {(data.walls?.bids?.length > 0 || data.walls?.asks?.length > 0) && (
            <div className="ob-walls">
              {data.walls.bids[0] && (
                <div className="ob-wall-card ob-wall-card--bid">
                  <Zap size={10} style={{ color: "#4ade80" }} />
                  <div className="ob-wall-body">
                    <span className="ob-wall-lbl">SUPPORT @ ${fmtPrice(data.walls.bids[0].price)}</span>
                    <span className="ob-wall-val" style={{ color: "#4ade80" }}>{fmtUsd(data.walls.bids[0].size_usd)}</span>
                    <span className="ob-wall-ex">{data.walls.bids[0].exchanges?.length || 0} exch</span>
                  </div>
                </div>
              )}
              {data.walls.asks[0] && (
                <div className="ob-wall-card ob-wall-card--ask">
                  <Zap size={10} style={{ color: "#ff5d6c" }} />
                  <div className="ob-wall-body">
                    <span className="ob-wall-lbl">RESISTANCE @ ${fmtPrice(data.walls.asks[0].price)}</span>
                    <span className="ob-wall-val" style={{ color: "#ff5d6c" }}>{fmtUsd(data.walls.asks[0].size_usd)}</span>
                    <span className="ob-wall-ex">{data.walls.asks[0].exchanges?.length || 0} exch</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Orderbook table — asks (top, descending price) + spread row + bids (bottom, descending price) */}
          <div className="ob-table">
            {/* Asks (resistance) — show in descending price (farthest first, mark price last) */}
            <div className="ob-side ob-side--ask">
              {(data.asks || []).slice().reverse().map((a, i) => {
                const pct = (a.size_usd / maxSize) * 100;
                return (
                  <div key={`a-${i}`} className={`ob-row ob-row--ask${a.is_wall ? " ob-row--wall" : ""}`}
                    title={`${a.exchanges?.length} exchanges: ${a.exchanges?.join(", ")}`}>
                    <div className="ob-bar-bg ob-bar-bg--ask" style={{ width: `${pct}%` }} />
                    <span className="ob-row-price">${fmtPrice(a.price)}</span>
                    <span className="ob-row-size">{a.size?.toFixed(3)}</span>
                    <span className="ob-row-usd">{fmtUsd(a.size_usd)}</span>
                    <span className="ob-row-exch">{a.exchanges?.length}x</span>
                    {a.is_wall && <span className="ob-row-wall-badge" style={{ color: "#ff5d6c" }}>▲</span>}
                  </div>
                );
              })}
            </div>

            {/* Mark price separator */}
            <div className="ob-mark-row">
              <span className="ob-mark-lbl">MARK</span>
              <span className="ob-mark-price">${fmtPrice(data.mark_price)}</span>
              <span className="ob-mark-spread">spread {data.spread_pct}%</span>
            </div>

            {/* Bids (support) — show in descending price (mark price first, farthest last) */}
            <div className="ob-side ob-side--bid">
              {(data.bids || []).map((b, i) => {
                const pct = (b.size_usd / maxSize) * 100;
                return (
                  <div key={`b-${i}`} className={`ob-row ob-row--bid${b.is_wall ? " ob-row--wall" : ""}`}
                    title={`${b.exchanges?.length} exchanges: ${b.exchanges?.join(", ")}`}>
                    <div className="ob-bar-bg ob-bar-bg--bid" style={{ width: `${pct}%` }} />
                    <span className="ob-row-price">${fmtPrice(b.price)}</span>
                    <span className="ob-row-size">{b.size?.toFixed(3)}</span>
                    <span className="ob-row-usd">{fmtUsd(b.size_usd)}</span>
                    <span className="ob-row-exch">{b.exchanges?.length}x</span>
                    {b.is_wall && <span className="ob-row-wall-badge" style={{ color: "#4ade80" }}>▼</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Exchange chips */}
          <div className="ob-exchanges">
            {(data.exchanges_attempted || []).map(ex => {
              const isUsed = data.exchanges_used?.includes(ex);
              return (
                <span key={ex} className={`ob-ex-chip${isUsed ? " is-used" : " is-missing"}`}>
                  <span className="ob-ex-dot" style={{ background: isUsed ? "#4ade80" : "#ff5d6c" }} />
                  {ex}
                </span>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
